import { useGetTransactionsTransactionRefSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense } from 'react';
import { makePath } from 'utils/routes';
import { Link } from 'wouter';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { kLabelColumnWidth } from '@fragment/ui/src/components/Payments/LayoutRow';

import { DynamicInvoiceIcon } from './DynamicInvoiceIcon';
import { FormatBillingAmount } from './formatBillingAmount';

type Props = {
  workspaceId: string;
  transactionId: string;
};

/** Small triangle badge — same as nav and recon list */
const TriangleBadge = ({ size = 14 }: { size?: number }) => {
  const points = `${size / 2},1 ${size - 1},${size - 1} 1,${size - 1}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
    >
      <polygon
        points={points}
        fill="rgb(var(--colour-main-500))"
        stroke="rgb(var(--colour-main-500))"
        strokeWidth={1}
        strokeLinejoin="round"
      />
    </svg>
  );
};

const TransactionDetailPageContent = ({
  workspaceId,
  transactionId,
}: Props): JSX.Element => {
  useDocumentTitle('Transaction');

  const { data } = useGetTransactionsTransactionRefSuspense(transactionId);
  const transaction = data.data;

  const code = transaction.currency as CurrencyCode;
  const precision = currencyPrecisionMap[code] || 2;
  const isUnreconciled = transaction.unallocated_amount !== '0';

  const handleBack = () => {
    window.history.back();
  };

  return (
    <PaymentViewLayout
      title={transaction.external_id}
      onBack={handleBack}
      primaryAction={
        isUnreconciled
          ? { title: 'Reconcile', onClick: () => {}, disabled: true }
          : undefined
      }
    >
      <div className="text-xmd font-helvetica flex flex-col w-full">
        {/* Title row */}
        <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
          <div className="flex items-start gap-x-f1 p-[13.5px] flex-1 min-w-0 h-full">
            <div className="flex items-center gap-x-[8px] shrink-0">
              <TriangleBadge size={24} />
              <span className="text-lg text-main">
                {transaction.external_id}
              </span>
            </div>
          </div>
        </div>

        {/* ID row */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
          <div
            className={clsx(
              'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
              kLabelColumnWidth
            )}
          >
            ID
          </div>
          <div className="px-[13.5px] text-xmd text-main flex-1">
            {transaction.id}
          </div>
        </div>

        {/* Amount row */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
          <div
            className={clsx(
              'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
              kLabelColumnWidth
            )}
          >
            Amount
          </div>
          <div className="px-[13.5px] text-xmd text-main flex-1">
            <FormatBillingAmount
              amount={Math.abs(Number(transaction.amount))}
              currencyCode={code}
              currencyPrecision={precision}
            />
          </div>
        </div>

        {/* Unallocated row */}
        <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
          <div
            className={clsx(
              'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
              kLabelColumnWidth
            )}
          >
            Unallocated
          </div>
          <div className="px-[13.5px] text-xmd text-main flex-1">
            <FormatBillingAmount
              amount={Math.abs(Number(transaction.unallocated_amount))}
              currencyCode={code}
              currencyPrecision={precision}
            />
          </div>
        </div>

        {/* Posted row — double height */}
        <div className="flex items-start border-b-[0.5px] border-main-300 w-full h-f8">
          <div
            className={clsx(
              'px-[13.5px] pt-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
              kLabelColumnWidth
            )}
          >
            Posted
          </div>
          <div className="px-[13.5px] pt-[13.5px] text-xmd text-main flex-1">
            {new Date(transaction.posted)
              .toDateString()
              .split(' ')
              .slice(1)
              .join(' ')}
          </div>
        </div>

        {/* Allocations label */}
        <div className="flex items-center justify-between border-b-[0.5px] border-main-200 w-full h-f4 px-[13.5px]">
          <span className="text-xmd text-main-500">Allocations</span>
          <span className="text-xmd text-main-500">Amount</span>
        </div>

        {/* Allocations list */}
        {transaction.allocations.length === 0 ? (
          <div className="flex items-center gap-x-[8px] px-[13.5px] h-f4 text-xmd text-main-500">
            <DynamicInvoiceIcon colors={['#D9D9D9']} height={14} />
            <span>No invoices</span>
          </div>
        ) : (
          transaction.allocations.map((allocation) => (
            <Link
              key={`${allocation.invoice_id}-${allocation.amount}`}
              href={makePath(
                [
                  'w',
                  workspaceId,
                  'payments',
                  'invoices',
                  allocation.invoice_id,
                ],
                { absolute: true }
              )}
            >
              <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4 hover:bg-negative cursor-pointer">
                <div
                  className={clsx(
                    'px-[13.5px] text-xmd text-main border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center gap-x-[8px]',
                    kLabelColumnWidth
                  )}
                >
                  <DynamicInvoiceIcon colors={[]} height={14} />
                  <span className="truncate">{allocation.invoice_id}</span>
                </div>
                <div className="px-[13.5px] text-xmd text-main flex-1 flex items-center justify-between">
                  <span className="text-main-500">{allocation.type}</span>
                  <FormatBillingAmount
                    amount={Math.abs(Number(allocation.amount))}
                    currencyCode={code}
                    currencyPrecision={precision}
                  />
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </PaymentViewLayout>
  );
};

const noop = (): void => {};

const TransactionDetailLoading = (): JSX.Element => (
  <PaymentViewLayout title="" onBack={noop}>
    <div className="text-xmd font-helvetica flex flex-col w-full">
      {/* Title row */}
      <div className="flex w-full items-start border-b-[0.5px] border-main-200 h-f12">
        <div className="flex items-start gap-x-f1 p-[13.5px] flex-1 min-w-0 h-full">
          <div className="flex items-center gap-x-[8px] shrink-0">
            <TriangleBadge size={24} />
            <span className="text-lg text-transparent">Loading</span>
          </div>
        </div>
      </div>

      {/* ID row */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
        <div
          className={clsx(
            'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
            kLabelColumnWidth
          )}
        >
          ID
        </div>
        <div className="px-[13.5px] flex-1" />
      </div>

      {/* Amount row */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
        <div
          className={clsx(
            'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
            kLabelColumnWidth
          )}
        >
          Amount
        </div>
        <div className="px-[13.5px] flex-1" />
      </div>

      {/* Unallocated row */}
      <div className="flex items-center border-b-[0.5px] border-main-200 w-full h-f4">
        <div
          className={clsx(
            'px-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full flex items-center',
            kLabelColumnWidth
          )}
        >
          Unallocated
        </div>
        <div className="px-[13.5px] flex-1" />
      </div>

      {/* Posted row — double height */}
      <div className="flex items-start border-b-[0.5px] border-main-300 w-full h-f8">
        <div
          className={clsx(
            'px-[13.5px] pt-[13.5px] text-xmd text-main-500 border-r-[0.5px] border-main-200 shrink-0 h-full',
            kLabelColumnWidth
          )}
        >
          Posted
        </div>
        <div className="px-[13.5px] pt-[13.5px] flex-1" />
      </div>

      {/* Allocations label */}
      <div className="flex items-center justify-between border-b-[0.5px] border-main-200 w-full h-f4 px-[13.5px]">
        <span className="text-xmd text-main-500">Allocations</span>
        <span className="text-xmd text-main-500">Amount</span>
      </div>
    </div>
  </PaymentViewLayout>
);

export const TransactionDetailPage = ({
  workspaceId,
  transactionId,
}: Props): JSX.Element => (
  <Suspense fallback={<TransactionDetailLoading />}>
    <TransactionDetailPageContent
      workspaceId={workspaceId}
      transactionId={transactionId}
    />
  </Suspense>
);
